import { ExportOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Flex, Radio, Skeleton, Space, Typography } from "antd"
import { type CheckboxOptionType } from "antd/es/checkbox/Group"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"

import { SearchBar } from "../../../../../../../components/Input/SearchBar.tsx"
import type { ProjectInvoiceFilters } from "../../../../../redux/project.types.ts"

interface ProjectViewInvoicesHeaderProps {
  filters: ProjectInvoiceFilters
  options: Record<string, BaseOptionType[]>
  isLocked?: boolean
  isEmptyList: boolean
  fetchingOptions: boolean
  onFilterChange: (data: Partial<ProjectInvoiceFilters>) => void
  onExport: () => Promise<void>
}

export function ProjectViewInvoicesHeader({
  filters,
  options,
  isEmptyList,
  isLocked = false,
  fetchingOptions,
  onFilterChange,
  onExport
}: ProjectViewInvoicesHeaderProps) {
  const invoiceType = options?.["type"] || []
  const invoiceStatus = options?.["status"] || []

  const types: CheckboxOptionType[] = useMemo(() => {
    return [
      { label: "All", value: null },
      ...invoiceType
    ] as CheckboxOptionType[]
  }, [invoiceType])

  const statuses: CheckboxOptionType[] = useMemo(() => {
    const mutatedOptions = invoiceStatus?.map((o) => ({
      ...o,
      disabled: false
    }))

    return [
      { label: "All", value: null },
      ...mutatedOptions
    ] as CheckboxOptionType[]
  }, [invoiceStatus])

  const onFilterChangeHandler =
    (key: keyof ProjectInvoiceFilters) => (value: unknown) => {
      onFilterChange({ [key]: value || null })
    }

  return (
    <Flex gap="small" vertical>
      <Flex justify="space-between" align="center">
        <Typography.Title level={4}>Invoices</Typography.Title>

        <Space>
          <ExportButton onClick={onExport} disabled={isLocked || isEmptyList} />
        </Space>
      </Flex>

      {fetchingOptions ? (
        <Skeleton.Node active className={"h-8 w-full"} />
      ) : (
        <Flex gap={12} align="center" justify={"space-between"} wrap="wrap">
          <Flex gap={12} align="center" wrap="wrap">
            <Radio.Group
              disabled={isLocked}
              value={filters?.type}
              onChange={(e) => onFilterChange({ type: e.target.value })}
              buttonStyle="solid"
              optionType={"button"}
              options={types}
            />

            <Radio.Group
              disabled={isLocked}
              value={filters?.status}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              buttonStyle="solid"
              optionType={"button"}
              options={statuses}
            />
          </Flex>

          <SearchBar
            disabled={isLocked}
            className={"md:w-1/3 flex-none"}
            placeholder="Search Invoices..."
            defaultValue={filters?.q || ""}
            prefix={<SearchOutlined />}
            onSearch={onFilterChangeHandler("q")}
          />
        </Flex>
      )}
    </Flex>
  )
}

interface ExportButtonProps {
  disabled?: boolean
  onClick?: () => Promise<void>
}

const ExportButton = ({ onClick, disabled }: ExportButtonProps) => {
  const [exporting, setExporting] = useState(false)

  const onClickHandler = async () => {
    try {
      setExporting(true)
      await onClick?.()
    } finally {
      setExporting(false)
    }
  }

  return (
    <Button
      loading={exporting}
      type="primary"
      icon={<ExportOutlined />}
      disabled={disabled}
      onClick={onClickHandler}
    >
      Export
    </Button>
  )
}
